import { useMemo, useRef, useState } from 'react';

import { useDismissOnEscape } from './dashboard/FilterDropdown.jsx';

// T366 — pick one application out of eighty-seven, by typing.
//
// The unlinked queue used a native <select>. For the 23 messages the matcher
// narrows to a handful that was fine, because the chips above it carry those.
// For the other 20 — the ones from hr-manager.net and ashbyhq.com, whose
// envelopes name no company at all — it was an 87-row list with no way in
// except scrolling, and the operator said so.
//
// Follows FilterDropdown rather than inventing a second dropdown: same trigger,
// same outside-click backdrop, same search-above-the-list, same role="listbox"
// and "No match", same Escape handling — its hook is imported, not copied.
// That one is a multi-select facet and this is a single choice, which is the
// only reason it is a separate component.

export default function ApplicationPicker({ candidatures, onPick, disabled, suggestedIds }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);
  // `close`, not a bare setOpen(false): Escape has to clear the query too, or
  // reopening starts with a stale filter that hides the row he came back for.
  const close = () => {
    setOpen(false);
    setQuery('');
  };
  useDismissOnEscape(open, close, triggerRef);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = needle
      ? candidatures.filter((c) =>
          [c.company, c.title, c.status].some((v) => String(v ?? '').toLowerCase().includes(needle)),
        )
      : candidatures;
    // What the rules thought plausible floats to the top, even here. He opened
    // the full list because the chips were wrong, but "wrong" usually means
    // "not that one of the five" rather than "none of the five".
    if (!suggestedIds?.size) return matches;
    return [...matches].sort((a, b) => Number(suggestedIds.has(b.id)) - Number(suggestedIds.has(a.id)));
  }, [candidatures, query, suggestedIds]);

  return (
    <div className="relative mt-1.5">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-left text-xs text-slate-300 transition hover:border-slate-500 disabled:opacity-50"
      >
        Choose an application… <span aria-hidden="true" className="float-right text-slate-500">▾</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={close} aria-hidden="true" />
          <div className="absolute left-0 right-0 z-40 mt-1 rounded-md border border-slate-700 bg-slate-900 p-1 shadow-lg">
            <input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type a company or a role…"
              aria-label="Search applications"
              className="mb-1 w-full rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-xs text-slate-100 focus:border-brand-500 focus:outline-none"
            />
            <div role="listbox" className="max-h-72 overflow-y-auto">
              {shown.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">No match</div>}
              {shown.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => {
                    close();
                    onPick(c.id);
                  }}
                  className="flex w-full items-baseline gap-2 rounded px-2 py-1.5 text-left text-xs text-slate-300 transition hover:bg-slate-800"
                >
                  <span className="shrink-0 font-medium text-slate-200">{c.company}</span>
                  {c.title && <span className="min-w-0 flex-1 truncate text-slate-500">{c.title}</span>}
                  {/* T478 — a row he drafted an application for but never
                      marked applied is offered here, and said so plainly.
                      Picking it links the mail; it does not move the stage
                      (T392), so the usual proposal still asks him. */}
                  {c.notYetApplied && (
                    <span className="shrink-0 rounded border border-amber-800 px-1 text-[10px] text-amber-400">
                      not marked applied
                    </span>
                  )}
                  <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-slate-500">
                    {c.status}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
